import { useAuthStore } from "../features/auth/store.ts";
import { connectGoogleDrive } from "./firebase.ts";
import { ApiError } from "./http.ts";

/** True for a Google Docs/Drive link (the only links context import accepts). */
export function isDriveUrl(url: string): boolean {
  return /(?:docs|drive)\.google\.com/i.test(url);
}

/**
 * Run a Drive-import API call, connecting Google Drive on demand and retrying once
 * on a 401 (expired/revoked token). The access token is cached in the auth store
 * (and auto-attached as X-Drive-Token by http.ts) but also passed explicitly to the
 * call to avoid any store-timing race. Mirrors AdminScreen.onAddLink. Reused by the
 * Discovery, pre-meeting, and live-meeting context surfaces.
 */
export async function runDriveImport<T>(fn: (token: string) => Promise<T>): Promise<T> {
  let token = useAuthStore.getState().googleAccessToken ?? "";
  if (!token) {
    token = await connectGoogleDrive();
    useAuthStore.getState().setGoogleAccessToken(token);
  }
  try {
    return await fn(token);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      const fresh = await connectGoogleDrive();
      useAuthStore.getState().setGoogleAccessToken(fresh);
      return await fn(fresh);
    }
    throw e;
  }
}

/** Whether the user already has a Drive access token (drives the button label). */
export function hasDriveToken(): boolean {
  return Boolean(useAuthStore.getState().googleAccessToken);
}
